/**
 * PWA AsyncStorage — localStorage-backed stand-in for
 * `@react-native-async-storage/async-storage` (Vite alias).
 * Includes multiGet/multiSet/multiRemove used by dashboard hooks.
 */
const memory = new Map<string, string>();

async function getItem(key: string): Promise<string | null> {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

async function setItem(key: string, value: string): Promise<void> {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    memory.set(key, value);
  }
}

async function removeItem(key: string): Promise<void> {
  try {
    window.localStorage.removeItem(key);
  } catch {
    memory.delete(key);
  }
}

async function clear(): Promise<void> {
  try {
    window.localStorage.clear();
  } catch {
    memory.clear();
  }
}

async function getAllKeys(): Promise<readonly string[]> {
  try {
    return Object.keys(window.localStorage);
  } catch {
    return Array.from(memory.keys());
  }
}

async function multiGet(keys: readonly string[]): Promise<readonly [string, string | null][]> {
  const out: [string, string | null][] = [];
  for (const key of keys) {
    out.push([key, await getItem(key)]);
  }
  return out;
}

async function multiSet(keyValuePairs: readonly [string, string][]): Promise<void> {
  for (const [key, value] of keyValuePairs) {
    await setItem(key, value);
  }
}

async function multiRemove(keys: readonly string[]): Promise<void> {
  for (const key of keys) {
    await removeItem(key);
  }
}

async function mergeItem(key: string, value: string): Promise<void> {
  const prev = await getItem(key);
  if (prev == null) {
    await setItem(key, value);
    return;
  }
  try {
    const merged = { ...JSON.parse(prev), ...JSON.parse(value) };
    await setItem(key, JSON.stringify(merged));
  } catch {
    await setItem(key, value);
  }
}

const AsyncStorage = {
  getItem,
  setItem,
  removeItem,
  clear,
  getAllKeys,
  multiGet,
  multiSet,
  multiRemove,
  mergeItem,
};

export {
  getItem,
  setItem,
  removeItem,
  clear,
  getAllKeys,
  multiGet,
  multiSet,
  multiRemove,
  mergeItem,
};

export default AsyncStorage;
